import { useState, useEffect, useMemo, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { useRivalDensity, type RivalDensityRow } from '../hooks/useRivalDensity'
import { SectionLabel, Pill } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

interface Location { id: string; name: string }

type Metric = 'brand' | 'sku'
type TypeFilter = 'both' | 'storefront' | 'delivery'

// Overlap bands — data, not advice. A rival carrying ≥⅔ of your catalog competes
// hard regardless of distance; ≤⅓ barely overlaps. The middle is left unlabeled.
const HIGH = 0.5
const LOW = 0.2
const COVERAGE_PRIORITY = 0.4

function metricVal(r: RivalDensityRow, m: Metric): number {
  return m === 'brand' ? r.brand_overlap : r.sku_overlap
}
function metricShared(r: RivalDensityRow, m: Metric): number {
  return m === 'brand' ? r.shared_brands : r.shared_skus
}

function overlapColor(v: number): string {
  if (v >= HIGH) return 'var(--danger)' // they carry most of what you carry
  if (v <= LOW) return 'var(--text-3)'
  return 'var(--warm)'
}

function exportCSV(rivals: RivalDensityRow[], data: { own_brand_total: number; own_sku_total: number }, locationName: string) {
  const header = ['Rival', 'Type', 'Distance (mi)', `Brand overlap % (of ${data.own_brand_total})`, 'Shared brands', `SKU overlap % (of ${data.own_sku_total})`, 'Shared SKUs', 'Their menu (SKUs)', 'Tracked'].join(',')
  const rows = rivals.map((r) =>
    [
      `"${r.name.replace(/"/g, '""')}"`,
      r.business_type ?? '',
      r.distance_mi != null ? r.distance_mi.toFixed(1) : '',
      Math.round(r.brand_overlap * 100),
      r.shared_brands,
      Math.round(r.sku_overlap * 100),
      r.shared_skus,
      r.rival_sku_count,
      r.tracked ? 'tracked' : 'no',
    ].join(',')
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `rival-density-${locationName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Overlap bar ─────────────────────────────────────────────────────────────
function OverlapBar({ v, color }: { v: number; color: string }) {
  const pct = Math.max(0, Math.min(1, v)) * 100
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 150 }}>
      <div style={{ flex: 1, height: 7, borderRadius: 4, background: 'var(--surface-3)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .3s' }} />
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, color: 'var(--text-1)', minWidth: 38, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(pct)}%
      </div>
    </div>
  )
}

// ── Rival row ───────────────────────────────────────────────────────────────
// ROW_GRID — one shared column grid for the header, uncovered rows, and covered
// rows so the whole thing reads as a single dense table. Leading rank column.
const ROW_GRID = '30px minmax(0,1fr) 84px 170px 100px 96px 16px'
// A selectable master row. Clicking it selects the rival in the right pane —
// there is NO inline expansion. `selected` drives a restrained active state
// (left accent rail + tint + bolder name); `muted` quiets covered rows.
function RivalRow({ r, metric, ownTotal, onClick, rank, selected = false, muted = false }: { r: RivalDensityRow; metric: Metric; ownTotal: number; onClick: () => void; rank?: number; selected?: boolean; muted?: boolean }) {
  const v = metricVal(r, metric)
  const shared = metricShared(r, metric)
  const color = overlapColor(v)
  const isTop = rank === 1 // subtle first-target emphasis — inside the row family
  const baseBg = selected ? 'var(--surface-2)' : 'transparent'
  const nameColor = muted && !selected ? 'var(--text-2)' : 'var(--text-1)'
  const previousOverlap = r.previous ? (metric === 'brand' ? r.previous.brand_overlap : r.previous.sku_overlap) : null
  const overlapDelta = previousOverlap == null ? null : Math.round((v - previousOverlap) * 100)
  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid', gridTemplateColumns: ROW_GRID, gap: 12, alignItems: 'center',
        padding: '5px 14px', cursor: 'pointer', borderTop: '1px solid var(--border)',
        borderLeft: selected ? '3px solid var(--accent)' : '3px solid transparent',
        transition: 'background .12s', background: baseBg, opacity: muted && !selected ? 0.82 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = baseBg }}
    >
      {/* Rank */}
      <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, textAlign: 'right', color: isTop ? 'var(--warm)' : 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
        {rank != null ? rank : '·'}
      </div>
      {/* Name + type */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: selected ? 800 : isTop ? 800 : 700, color: nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
            {r.business_type === 'delivery' ? 'delivery' : 'storefront'}
          </span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, color: 'var(--text-3)' }}>· {r.rival_sku_count} SKUs</span>
          {!muted && <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, fontWeight: 700, color: overlapDelta == null ? 'var(--warm)' : overlapDelta > 0 ? 'var(--danger)' : overlapDelta < 0 ? 'var(--accent)' : 'var(--text-3)' }}>· {overlapDelta == null ? 'NEW BASELINE' : overlapDelta === 0 ? 'UNCHANGED' : `${overlapDelta > 0 ? '+' : ''}${overlapDelta} PTS`}</span>}
        </div>
      </div>
      {/* Distance */}
      <div style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)' }}>
        {r.distance_mi != null ? `${r.distance_mi.toFixed(1)} mi` : '—'}
      </div>
      {/* Overlap bar */}
      <OverlapBar v={v} color={color} />
      {/* Shared count */}
      <div style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-1)' }}>
        {shared}<span style={{ color: 'var(--text-3)', fontWeight: 400 }}>/{ownTotal}</span>
      </div>
      {/* Tracked state */}
      <div style={{ textAlign: 'right' }}>
        {r.tracked ? (
          <Pill variant="chip" tone="tracking">Tracked</Pill>
        ) : (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>—</span>
        )}
      </div>
      {/* Selection indicator — points into the inspector */}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={selected ? 'var(--accent)' : 'var(--text-3)'} strokeWidth="2.5" style={{ justifySelf: 'end', transition: 'stroke .15s' }}><polyline points="9 18 15 12 9 6" /></svg>
    </div>
  )
}

// ── Zone header ───────────────────────────────────────────────────────────────
// Splits the table into "the decision" (uncovered) and "for reference" (covered).
function ZoneHeader({ label, count, note, tone }: { label: string; count: number; note: string; tone: 'primary' | 'muted' }) {
  const accent = tone === 'primary' ? 'var(--warm)' : 'var(--text-3)'
  return (
    <div
      style={{
        display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap',
        padding: tone === 'primary' ? '4px 16px 8px' : '18px 16px 8px',
        marginTop: tone === 'primary' ? 0 : 6,
        borderTop: tone === 'primary' ? 'none' : '1px solid var(--border)',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 2, background: accent, alignSelf: 'center', flexShrink: 0 }} />
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: tone === 'primary' ? 'var(--text-1)' : 'var(--text-2)' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, fontWeight: 700, color: accent, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      <span style={{ fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--text-3)' }}>{note}</span>
    </div>
  )
}

// ── Zone eyebrow — small mono label above each detail zone ───────────────────
function ZoneEyebrow({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 4 }}>
      {children}
    </div>
  )
}

// ── Inspector section + fact row helpers ─────────────────────────────────────
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ paddingTop: 6, marginTop: 6, borderTop: '1px solid var(--border)' }}>
      <ZoneEyebrow>{label}</ZoneEyebrow>
      {children}
    </div>
  )
}
function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{k}</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  )
}

// ── Rival inspector — the sticky right pane for the selected rival ────────────
// Fixed section order per spec: name, subline, Why it matters, Overlap snapshot,
// Coverage, Recommended next step, and one primary Open-profile action. Every
// value is a real field (brand/sku overlap %, shared/own counts, tracked/blocked).
// No bars, no pills, no interpretive labels, no invented data.
function RivalInspector({ r, ownBrandTotal, ownSkuTotal, onOpenProfile, onTrack }: {
  r: RivalDensityRow
  ownBrandTotal: number
  ownSkuTotal: number
  onOpenProfile: () => void
  onTrack: () => void
}) {
  const brandPct = Math.round(r.brand_overlap * 100)
  const skuPct = ownSkuTotal > 0 ? Math.round(r.sku_overlap * 100) : null
  const covered = r.tracked
  const coverage = r.tracked ? 'Already tracked' : 'Not tracked'
  const coverageColor = r.tracked ? 'var(--accent)' : 'var(--text-1)'
  const next = covered ? 'Already tracked' : 'Track this rival'
  const type = r.business_type === 'delivery' ? 'Delivery' : 'Storefront'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 16px 12px' }}>
      {/* 1 — Name */}
      <div style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
        {r.name}
      </div>
      {/* 2 — Muted subline */}
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', marginTop: 4, letterSpacing: '0.03em' }}>
        {type.toUpperCase()} · {r.rival_sku_count} SKUs · {r.distance_mi != null ? `${r.distance_mi.toFixed(1)} mi` : '—'}
      </div>

      {/* 3 — Why it matters */}
      <Section label="Why it matters">
        <div style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--text-1)' }}>
          <strong style={{ fontWeight: 800 }}>{r.shared_brands}</strong> of your{' '}
          <strong style={{ fontWeight: 800 }}>{ownBrandTotal}</strong> brands overlap here.
          {skuPct != null && (
            <> Exact SKU overlap is <strong style={{ fontWeight: 800 }}>{skuPct}%</strong>.</>
          )}
        </div>
      </Section>

      {/* 4 — Overlap snapshot */}
      <Section label="Overlap snapshot">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Fact k="Brand overlap" v={`${brandPct}%`} />
          <Fact k="Exact SKU overlap" v={skuPct != null ? `${skuPct}%` : '—'} />
        </div>
      </Section>

      {/* 5 — Coverage */}
      <Section label="Coverage">
        <div style={{ fontSize: 14, fontWeight: 700, color: coverageColor }}>{coverage}</div>
      </Section>

      {/* 6 — Recommended next step */}
      <Section label="Recommended next step">
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>{next}</div>
      </Section>

      {/* 7 — Resolve the coverage gap here; profile stays available as evidence. */}
      <div style={{ marginTop: 10, display: 'flex', gap: 7 }}>
        {covered ? (
          <button onClick={onOpenProfile} style={{ width: '100%', cursor: 'pointer', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 800 }}>Open profile →</button>
        ) : (
          <>
            <button onClick={onTrack} style={{ flex: 1, cursor: 'pointer', background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)', borderRadius: 'var(--r-sm)', padding: '9px 10px', fontFamily: 'var(--sans)', fontSize: 12.5, fontWeight: 800 }}>Track rival</button>
          </>
        )}
      </div>
      {!covered && <button onClick={onOpenProfile} style={{ marginTop: 7, background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 9.5 }}>Inspect profile before deciding →</button>}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RivalDensity() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [radius, setRadius] = useState(10)
  const [metric, setMetric] = useState<Metric>('brand')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('both')
  const [search, setSearch] = useState('')
  const [onlyUntracked, setOnlyUntracked] = useState(false)
  const [showCovered, setShowCovered] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null) // the one rival selected in the right-pane inspector
  const [pendingCoverage, setPendingCoverage] = useState<{ rival: RivalDensityRow; action: 'track' } | null>(null)
  const [coverageBusy, setCoverageBusy] = useState(false)
  const [coverageError, setCoverageError] = useState<string | null>(null)
  const [coverageSuccess, setCoverageSuccess] = useState<string | null>(null)

  const { data, loading, error, refresh } = useRivalDensity(selectedLocation, radius, typeFilter)

  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d) => {
        const locs: Location[] = d.locations || []
        setLocations(locs)
        if (locs.length > 0 && !selectedLocation) setSelectedLocation(locs[0].id)
      })
      .catch(() => {})
  }, [authFetch])

  const M = data?.total_rivals ?? 0
  const ownBrandTotal = data?.own_brand_total ?? 0
  const ownSkuTotal = data?.own_sku_total ?? 0
  const ownTotal = metric === 'brand' ? ownBrandTotal : ownSkuTotal
  const hasOwnMenu = ownBrandTotal > 0 || ownSkuTotal > 0
  const allRivals = data?.rivals ?? []

  const rivals = useMemo(() => {
    let list = allRivals
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter((r) => r.name.toLowerCase().includes(q))
    }
    if (onlyUntracked) list = list.filter((r) => !r.tracked)
    const sorted = [...list]
    sorted.sort(
      (a, b) =>
        metricVal(b, metric) - metricVal(a, metric) ||
        (a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9)
    )
    return sorted
  }, [allRivals, search, metric, onlyUntracked])

  // The actionable headline: high-overlap rivals not yet tracked.
  const worthAdding = useMemo(
    () => allRivals.filter((r) => metricVal(r, metric) >= HIGH && !r.tracked),
    [allRivals, metric]
  )
  const trackedCount = allRivals.filter((r) => r.tracked).length

  // Split the ranked list into the decision (rivals overlapping you that you
  // haven't acted on) and the reference set (already tracked). Both
  // keep the overlap sort — this only partitions, it never re-ranks.
  const exposed = useMemo(() => rivals.filter((r) => !r.tracked), [rivals])
  const covered = useMemo(() => rivals.filter((r) => r.tracked), [rivals])

  // Default-open the top uncovered rival, and reset to the new top whenever the
  // ranked queue changes (filters/search/radius/location/data). Keyed on the top
  // id so it does NOT fight a manual open of a different row.
  const topExposedId = exposed[0]?.competitor_id ?? null
  useEffect(() => { setOpenId(topExposedId) }, [topExposedId])

  const locationName = locations.find((l) => l.id === selectedLocation)?.name || 'Location'
  const goProfile = (id: string) => navigate(`/competitors/${id}${selectedLocation ? `?location_id=${selectedLocation}` : ''}`)
  // Master-detail: clicking a row SELECTS it (never toggles off) so the right
  // pane inspector is never blank while a rival exists.
  const selectRow = (id: string) => setOpenId(id)
  const selectedRival = rivals.find((r) => r.competitor_id === openId) ?? null
  // The inspector always shows something while a rival exists: the explicit
  // selection, else the top uncovered rival (covers the frame before the
  // auto-select effect runs). activeId drives the row highlight so it matches.
  const inspectorRival = selectedRival ?? exposed[0] ?? null
  const activeId = inspectorRival?.competitor_id ?? null
  const topGap = exposed[0] ?? null
  const topGapPrevious = topGap?.previous ?? null
  const topGapCurrentOverlap = topGap ? metricVal(topGap, metric) : 0
  const topGapPreviousOverlap = topGapPrevious ? (metric === 'brand' ? topGapPrevious.brand_overlap : topGapPrevious.sku_overlap) : null
  const topGapDeltaPoints = topGapPreviousOverlap == null ? null : Math.round((topGapCurrentOverlap - topGapPreviousOverlap) * 100)
  const topGapChange = topGapPreviousOverlap == null
    ? 'New comparison baseline'
    : topGapDeltaPoints === 0
      ? 'Overlap unchanged since the prior scan'
      : `Overlap ${topGapDeltaPoints! > 0 ? 'up' : 'down'} ${Math.abs(topGapDeltaPoints!)} point${Math.abs(topGapDeltaPoints!) === 1 ? '' : 's'} since the prior scan`
  const sep = <div style={{ width: 1, height: 20, background: 'var(--border-2)', flexShrink: 0, margin: '0 4px' }} />

  const applyCoverage = async () => {
    if (!pendingCoverage || !selectedLocation) return
    setCoverageBusy(true)
    setCoverageError(null)
    try {
      const response = await authFetch(`${API}/api/v1/locations/${selectedLocation}/competitors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ competitor_id: pendingCoverage.rival.competitor_id, slot_type: pendingCoverage.action }),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body.error || `Could not ${pendingCoverage.action} this rival.`)
      setCoverageSuccess(`${pendingCoverage.rival.name} is now tracked.`)
      setPendingCoverage(null)
      refresh()
    } catch (actionError) {
      setCoverageError(actionError instanceof Error ? actionError.message : 'Could not update coverage.')
    } finally {
      setCoverageBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── TOPBAR ── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0 12px' }}>
          <button
            onClick={() => navigate('/market/ranking')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', cursor: 'pointer', background: 'none', border: 'none', flexShrink: 0, padding: 0 }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            Market
          </button>
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1 }}>
              Rival Density
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 2 }}>
              {locationName.toUpperCase()} · {M} SHOPS IN RANGE · {radius}MI · RANKED BY CATALOG OVERLAP
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Radius</span>
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              style={{ background: 'var(--surface-3)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '6px 10px', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--sans)', cursor: 'pointer', fontWeight: 600 }}
            >
              {[5, 10, 15, 25, 40, 60].map((r) => <option key={r} value={r}>{r} mi</option>)}
            </select>
            {sep}
            <button
              className="btn"
              onClick={() => data && exportCSV(rivals, data, locationName)}
              disabled={!rivals.length}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', fontSize: 11, opacity: rivals.length ? 1 : 0.5 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export CSV
            </button>
            {locations.length > 1 && (
              <select
                value={selectedLocation || ''}
                onChange={(e) => setSelectedLocation(e.target.value)}
                style={{ background: 'var(--surface-3)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: '7px 13px', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--sans)', cursor: 'pointer' }}
              >
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        {/* Metric */}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Overlap</span>
        <div style={{ display: 'inline-flex', background: 'var(--surface)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: 2, gap: 2 }}>
          {([
            { v: 'brand' as Metric, label: 'By brand' },
            { v: 'sku' as Metric, label: 'By SKU' },
          ]).map((s) => (
            <button
              key={s.v}
              onClick={() => setMetric(s.v)}
              style={{
                padding: '5px 12px', borderRadius: 'calc(var(--r-sm) - 2px)', fontSize: 11, fontWeight: 600,
                fontFamily: 'var(--sans)', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                background: metric === s.v ? 'var(--accent)' : 'transparent',
                color: metric === s.v ? '#fff' : 'var(--text-2)', transition: 'var(--transition)',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        {sep}
        {/* Type */}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Type</span>
        <div style={{ display: 'inline-flex', background: 'var(--surface)', border: '1.5px solid var(--border-2)', borderRadius: 'var(--r-sm)', padding: 2, gap: 2 }}>
          {([
            { v: 'both' as TypeFilter, label: 'All' },
            { v: 'storefront' as TypeFilter, label: 'Storefront' },
            { v: 'delivery' as TypeFilter, label: 'Delivery' },
          ]).map((s) => (
            <button
              key={s.v}
              onClick={() => setTypeFilter(s.v)}
              style={{
                padding: '5px 12px', borderRadius: 'calc(var(--r-sm) - 2px)', fontSize: 11, fontWeight: 600,
                fontFamily: 'var(--sans)', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
                background: typeFilter === s.v ? 'var(--accent)' : 'transparent',
                color: typeFilter === s.v ? '#fff' : 'var(--text-2)', transition: 'var(--transition)',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        {sep}
        <button
          onClick={() => setOnlyUntracked((v) => !v)}
          style={{
            padding: '6px 12px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600,
            fontFamily: 'var(--sans)', cursor: 'pointer',
            border: `1.5px solid ${onlyUntracked ? 'var(--warm)' : 'var(--border-2)'}`,
            background: onlyUntracked ? 'var(--warm-soft)' : 'var(--surface)',
            color: onlyUntracked ? 'var(--warm)' : 'var(--text-2)',
          }}
        >
          Not tracked only
        </button>
        {sep}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a rival…"
          style={{ padding: '6px 11px', borderRadius: 'var(--r-sm)', fontSize: 11, border: '1.5px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'var(--sans)', minWidth: 160 }}
        />
        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          Overlap = share of <span style={{ color: 'var(--text-2)', fontWeight: 700 }}>your</span> {metric === 'brand' ? `${ownBrandTotal} brands` : `${ownSkuTotal} SKUs`} a rival also carries
        </div>
      </div>

      {/* ── CONTENT ── */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 13 }}>
          Scoring catalog overlap across nearby shops…
        </div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--danger)', fontFamily: 'var(--mono)', fontSize: 13, padding: 24, textAlign: 'center' }}>
          {error}
          <span style={{ color: 'var(--text-3)', fontSize: 11 }}>We'll keep collecting — check back after the next window.</span>
        </div>
      ) : !selectedLocation ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 14 }}>
          Add a location to rank nearby rivals by overlap.
        </div>
      ) : !hasOwnMenu ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--text-3)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Your menu isn't in range yet</div>
          <div style={{ fontSize: 13, maxWidth: 340, textAlign: 'center' }}>
            Overlap is measured against <strong>your own catalog</strong> for {locationName}. We haven't collected this location's menu yet — once we do, every nearby rival gets scored against it.
          </div>
        </div>
      ) : rivals.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--text-3)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>No rivals match</div>
          <div style={{ fontSize: 13, maxWidth: 320, textAlign: 'center' }}>
            {search.trim() || onlyUntracked
              ? 'No rivals match the current filters. Clear the search or filter.'
              : `No nearby shops with collected menus within ${radius} miles. Widen the radius or check back after the next collection window.`}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, padding: '12px 24px 16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Coverage decision brief — this page answers which intelligence gap
              to close, not which rival is the greatest pricing threat. */}
          <div data-testid="density-decision-brief" style={{ flexShrink: 0, display: 'flex', gap: 12, marginBottom: 12 }}>
            {topGap ? (
              <>
                <div style={{ flex: '1 1 64%', minWidth: 0, border: '1px solid var(--warm-soft)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--warm-soft)', color: 'var(--warm)', fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 800 }}>{exposed.length}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
                      <SectionLabel style={{ margin: 0, color: 'var(--warm)' }}>Top uncovered intelligence gap</SectionLabel>
                      <Pill tone={topGapDeltaPoints != null && topGapDeltaPoints > 0 ? 'warning' : 'neutral'} variant="chip" style={{ padding: '2px 7px' }}>{topGapChange}</Pill>
                    </div>
                    <div style={{ fontSize: 17, lineHeight: 1.2, fontWeight: 800, color: 'var(--text-1)' }}>Cover {topGap.name} first.</div>
                    <div style={{ marginTop: 4, fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-2)' }}>
                      {metricShared(topGap, metric)} of your {ownTotal} {metric === 'brand' ? 'brands' : 'SKUs'} overlap ({Math.round(topGapCurrentOverlap * 100)}%) · {topGap.distance_mi?.toFixed(1) ?? '—'} mi away · {topGap.business_type === 'delivery' ? 'delivery' : 'storefront'}
                    </div>
                  </div>
                </div>
                <div style={{ flex: '1 1 36%', minWidth: 260, border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', padding: '12px 15px' }}>
                  <SectionLabel style={{ marginBottom: 5 }}>Why this clears the priority bar</SectionLabel>
                  <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-2)' }}>
                    Priority means at least {Math.round(COVERAGE_PRIORITY * 100)}% brand overlap. This rival carries {Math.round(topGap.brand_overlap * 100)}% of your brands, so leaving it uncovered creates a material blind spot.
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, border: '1px solid var(--accent-soft)', borderRadius: 'var(--r-sm)', background: 'var(--accent-soft)', padding: '13px 16px' }}>
                <SectionLabel style={{ marginBottom: 4, color: 'var(--accent)' }}>Coverage complete</SectionLabel>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Every overlapping rival in this radius is already tracked. No action is needed.</div>
              </div>
            )}
          </div>

          {coverageSuccess && <div style={{ flexShrink: 0, marginBottom: 10, padding: '8px 12px', borderRadius: 'var(--r-sm)', background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: 12, fontWeight: 700 }}>{coverageSuccess}</div>}
          {/* One connected surface: a dense ranked queue on the left and a sticky
              decision inspector on the right. No inline row expansion. */}
          {/* Two associated cards (not one shared surface). Each hugs its own
              content: the left queue drives its height and scrolls when long;
              the inspector (alignSelf flex-start) ends at its own content so the
              area below it is ordinary page background, never a stranded blank
              band inside a shared bordered surface. The 12px gap + proximity
              keeps them reading as one master-detail workflow. */}
          <div style={{ display: 'flex', gap: 12, flex: 1, maxHeight: '100%', minHeight: 0, alignItems: 'flex-start' }}>

            {/* ── LEFT PANE — ranked queue (~60%) ── */}
            <div style={{ flex: '1 1 60%', minWidth: 0, maxHeight: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--surface)' }}>
              {/* Section header */}
              <div style={{ flexShrink: 0, padding: '10px 14px 9px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-1)' }}>Not yet covered</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{exposed.length}</span>
                </div>
                <div style={{ fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                  Nearby rivals overlapping your assortment that you have not tracked.
                </div>
              </div>
              {/* Column header — offset 3px to align with rows' accent rail */}
              <div style={{ flexShrink: 0, display: 'grid', gridTemplateColumns: ROW_GRID, gap: 12, padding: '6px 14px', borderLeft: '3px solid transparent', alignItems: 'center', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', textAlign: 'right' }}>#</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>Rival</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', textAlign: 'right' }}>Distance</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{metric === 'brand' ? 'Brand' : 'SKU'} overlap</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', textAlign: 'right' }}>Shared</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', textAlign: 'right' }}>Status</div>
                <div />
              </div>

              {/* Scrollable dense body: uncovered rows, then the covered drawer */}
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {exposed.length > 0 ? (
                  exposed.map((r, i) => (
                    <RivalRow
                      key={r.competitor_id}
                      r={r}
                      metric={metric}
                      ownTotal={ownTotal}
                      rank={i + 1}
                      selected={activeId === r.competitor_id}
                      onClick={() => selectRow(r.competitor_id)}
                    />
                  ))
                ) : (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-2)' }}>
                    Every overlapping rival in range is already tracked — you're covered here.
                  </div>
                )}

                {/* Covered drawer — collapsed by default, same row system, muted */}
                {!onlyUntracked && covered.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowCovered((s) => !s)}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', border: 'none', borderTop: '1px solid var(--border)', background: 'var(--surface)', padding: '5px 14px', opacity: 0.85 }}
                    >
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Already covered</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>{covered.length}</span>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: 10, color: 'var(--text-3)', opacity: 0.7 }}>· tracked</span>
                      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)' }}>{showCovered ? 'Hide' : 'Show'}</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2.5" style={{ transform: showCovered ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}><polyline points="9 18 15 12 9 6" /></svg>
                      </span>
                    </button>
                    {showCovered && covered.map((r) => (
                      <RivalRow
                        key={r.competitor_id}
                        r={r}
                        metric={metric}
                        ownTotal={ownTotal}
                        muted
                        selected={activeId === r.competitor_id}
                        onClick={() => selectRow(r.competitor_id)}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* ── RIGHT PANE — decision inspector (~40%, hugs its own content) ── */}
            <div style={{ flex: '1 1 40%', minWidth: 0, minHeight: 0, maxHeight: '100%', alignSelf: 'flex-start', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', overflowY: 'auto' }}>
              {inspectorRival ? (
                <RivalInspector
                  r={inspectorRival}
                  ownBrandTotal={ownBrandTotal}
                  ownSkuTotal={ownSkuTotal}
                  onOpenProfile={() => goProfile(inspectorRival.competitor_id)}
                  onTrack={() => { setCoverageError(null); setPendingCoverage({ rival: inspectorRival, action: 'track' }) }}
                />
              ) : (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>You're covered here</div>
                  <div style={{ fontSize: 13, maxWidth: 260 }}>Every overlapping rival in range is already tracked.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {pendingCoverage && (
        <div data-testid="density-coverage-confirm" onClick={() => !coverageBusy && setPendingCoverage(null)} style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(13,31,45,0.46)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: 'min(440px, 100%)', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', background: 'var(--surface)', boxShadow: '0 20px 60px rgba(13,31,45,0.24)', padding: 20 }}>
            <SectionLabel style={{ marginBottom: 6, color: 'var(--accent)' }}>Confirm tracking</SectionLabel>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Track {pendingCoverage.rival.name}?</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
              Tracking adds this rival to price, promotion, and alert monitoring for this location.
              {' '}Tracking uses an available rival allowance in this location's plan.
            </div>
            {coverageError && <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12 }}>{coverageError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button disabled={coverageBusy} onClick={() => setPendingCoverage(null)} style={{ padding: '8px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer', fontWeight: 700 }}>Cancel</button>
              <button disabled={coverageBusy} onClick={applyCoverage} style={{ padding: '8px 13px', borderRadius: 'var(--r-sm)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: coverageBusy ? 'wait' : 'pointer', fontWeight: 800 }}>
                {coverageBusy ? 'Updating…' : 'Confirm tracking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
